import { useCallback, useEffect, useRef, useState } from 'react'
import type { ActiveCall } from '../types'
import { CallBoostControl } from './CallBoostControl'

function formatDuration(startedAt: string): string {
  const seconds = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

interface CallInfoProps {
  call: ActiveCall
  onMute?: () => void
  onHangup?: () => void
  onAnswer?: () => void
  isMuted?: boolean
  audioState?: RTCPeerConnectionState | null
  // Live audio boost. Optional so parents that don't use boost still
  // compile, though in practice usePhoneCall always provides them.
  boostEnabled?: boolean
  boostGain?: number
  onBoostEnabledChange?: (v: boolean) => void
  onBoostGainChange?: (v: number) => void
}

const hangupIcon = (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
    <line x1="23" y1="1" x2="1" y2="23" />
  </svg>
)

export function CallInfo({
  call, onMute, onHangup, onAnswer, isMuted = false, audioState,
  boostEnabled = false, boostGain = 1,
  onBoostEnabledChange, onBoostGainChange,
}: CallInfoProps) {
  const [duration, setDuration] = useState(formatDuration(call.startedAt))
  const isRinging = call.state === 'ringing'
  const isDialing = call.state === 'dialing'
  const isPreConnect = isRinging || isDialing

  // Boost popover position (computed from button rect on open). null = closed.
  // Mirrors AudioPlayer.tsx popover pattern for consistency.
  const boostBtnRef = useRef<HTMLButtonElement>(null)
  const [boostPos, setBoostPos] = useState<{ top: number; right: number } | null>(null)
  const boostOpen = boostPos !== null
  const toggleBoostPopover = (): void => {
    if (boostOpen) {
      setBoostPos(null)
      return
    }
    const rect = boostBtnRef.current?.getBoundingClientRect()
    setBoostPos({
      top: rect ? rect.bottom + 4 : 4,
      right: rect ? window.innerWidth - rect.right : 0,
    })
  }
  // Stable ref so CallBoostControl's listener-effect doesn't re-attach
  // every second when the active-call duration tick re-renders CallInfo.
  const closeBoost = useCallback(() => setBoostPos(null), [])

  useEffect(() => {
    if (call.state !== 'active') return
    const interval = setInterval(() => setDuration(formatDuration(call.startedAt)), 1000)
    return () => clearInterval(interval)
  }, [call.startedAt, call.state])

  const borderColor = isRinging ? 'border-amber-500' : isDialing ? 'border-blue-500' : 'border-red-600'
  const dotColor = isRinging ? 'bg-amber-500' : isDialing ? 'bg-blue-500' : 'bg-red-600'
  const labelColor = isRinging ? 'text-amber-600' : isDialing ? 'text-blue-500' : 'text-red-600'
  const label = isRinging ? 'INCOMING CALL' : isDialing ? 'DIALING' : 'ACTIVE CALL'

  return (
    <div className={`border-l-4 ${borderColor} bg-white dark:bg-gray-900 px-5 py-4`}>
      {/* Top row: label + timer */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${dotColor} animate-pulse`} />
          <span className={`font-mono text-[11px] font-semibold tracking-wider ${labelColor}`}>
            {label}
          </span>
        </div>
        {!isPreConnect && (
          <span className="font-mono text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {duration}
          </span>
        )}
      </div>

      {/* Number */}
      <p className="font-mono text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
        {call.number}
      </p>

      {/* Audio connection indicator — only during active calls */}
      {call.state === 'active' && audioState && audioState !== 'connected' && (
        <p className={`font-mono text-[11px] mb-3 ${
          audioState === 'failed' ? 'text-red-500' :
          audioState === 'disconnected' ? 'text-red-400' :
          'text-gray-400'
        }`}>
          {audioState === 'failed' ? 'Audio connection failed' :
           audioState === 'disconnected' ? 'Audio disconnected' :
           'Connecting audio...'}
        </p>
      )}

      {/* Buttons */}
      <div className="flex gap-2">
        {isPreConnect && isRinging ? (
          <>
            {onAnswer && (
              <button
                onClick={onAnswer}
                className="flex items-center justify-center gap-2 flex-1 sm:flex-none px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Answer
              </button>
            )}
            {onHangup && (
              <button
                onClick={onHangup}
                className="flex items-center justify-center gap-2 flex-1 sm:flex-none px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
              >
                {hangupIcon}
                Decline
              </button>
            )}
          </>
        ) : (
          <>
            {onBoostEnabledChange && onBoostGainChange && (
              <button
                ref={boostBtnRef}
                onClick={toggleBoostPopover}
                aria-label={boostEnabled ? `Усиление ${Math.round(boostGain * 100)}%` : 'Усиление выключено'}
                aria-pressed={boostEnabled}
                data-testid="call-boost-trigger"
                className={`flex items-center justify-center gap-2 flex-1 sm:flex-none px-5 py-2.5 border text-sm font-medium transition-colors ${
                  boostEnabled
                    ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/20'
                    : 'border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
                {boostEnabled ? `${Math.round(boostGain * 100)}%` : 'Boost'}
              </button>
            )}
            {onMute && (
              <button
                onClick={onMute}
                className={`flex items-center justify-center gap-2 flex-1 sm:flex-none px-5 py-2.5 border text-sm font-medium transition-colors ${
                  isMuted
                    ? 'border-red-600 text-red-600 bg-red-50 dark:bg-red-950/20'
                    : 'border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {isMuted ? (
                    <>
                      <line x1="1" y1="1" x2="23" y2="23" />
                      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .7-.1 1.38-.3 2.02" />
                      <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                    </>
                  ) : (
                    <>
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                    </>
                  )}
                </svg>
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
            )}
            {onHangup && (
              <button
                onClick={onHangup}
                className="flex items-center justify-center gap-2 flex-1 sm:flex-none px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
              >
                {hangupIcon}
                Hang up
              </button>
            )}
          </>
        )}
      </div>

      {boostOpen && boostPos && onBoostEnabledChange && onBoostGainChange && (
        <CallBoostControl
          enabled={boostEnabled}
          gain={boostGain}
          onEnabledChange={onBoostEnabledChange}
          onGainChange={onBoostGainChange}
          position={{ top: boostPos.top, right: boostPos.right }}
          onClose={closeBoost}
          triggerRef={boostBtnRef}
          controlsTestId="call-boost"
        />
      )}
    </div>
  )
}
